import * as Menu from "components/MenuPanel/MenuPanel";
import usePublicnessState from "gui/browse-shared/usePublicnessState";
import useShowArchivedState from "gui/browse-shared/useShowArchivedState";
import useShowMaterializedViewsState from "gui/browse-shared/useShowMaterializedViewsState";
import { useIsFeatureEnabled } from "lib/hooks/useIsFeatureEnabled";
import useOrderState from "./useOrderState";
import type { TabName } from "./useActiveTab";

export interface Filter {
  freetext: string;
  owner?: {
    type: "user" | "team";
    handle: string;
  };
  publicness: "all" | "public" | "private";
  is_matview: boolean;
  is_archived: boolean;
}

interface Props {
  activeTab?: TabName;
}

export function Filters(props: Props) {
  const { activeTab } = props;

  const [showArchived, setShowArchived] = useShowArchivedState();
  const [publicness, setPublicness] = usePublicnessState();
  const [
    showMaterialized,
    setShowMaterialized,
  ] = useShowMaterializedViewsState();
  const [sort, setOrderUrl] = useOrderState();

  return (
    <Menu.Panel>
      <Menu.Section title="Sort by...">
        <Menu.ItemButton
          selected={sort.by === "created_at"}
          onClick={() => {
            setOrderUrl({
              by: "created_at",
              order: "order" in sort ? sort.order : "Desc",
            });
          }}
        >
          <span>Created date</span>
        </Menu.ItemButton>
        <Menu.ItemButton
          selected={sort.by === "updated_at"}
          onClick={() => {
            setOrderUrl({
              by: "updated_at",
              order: "order" in sort ? sort.order : "Desc",
            });
          }}
        >
          <span>Modified date</span>
        </Menu.ItemButton>
        <Menu.ItemButton
          selected={sort.by === "favorites"}
          onClick={() => {
            setOrderUrl({
              by: "favorites",
              time_range: "7d",
            });
          }}
        >
          <span>Favorites</span>
        </Menu.ItemButton>
      </Menu.Section>

      {/* Order by only available if we're not sorting by favorites */}
      {sort.by !== "favorites" && (
        <Menu.Section title="Order by...">
          <Menu.ItemButton
            selected={sort.order === "Desc"}
            onClick={() => {
              setOrderUrl({ by: sort.by, order: "Desc" });
            }}
          >
            <span>Descending</span>
          </Menu.ItemButton>
          <Menu.ItemButton
            selected={sort.order === "Asc"}
            onClick={() => {
              setOrderUrl({ by: sort.by, order: "Asc" });
            }}
          >
            <span>Ascending</span>
          </Menu.ItemButton>
        </Menu.Section>
      )}

      {sort.by === "favorites" && (
        <Menu.Section title="Time range…">
          <Menu.ItemButton
            onClick={() => {
              setOrderUrl({ by: "favorites", time_range: "24h" });
            }}
            selected={sort.time_range === "24h"}
          >
            24 hours
          </Menu.ItemButton>
          <Menu.ItemButton
            onClick={() => {
              setOrderUrl({ by: "favorites", time_range: "7d" });
            }}
            selected={sort.time_range === "7d"}
          >
            7 days
          </Menu.ItemButton>
          <Menu.ItemButton
            onClick={() => {
              setOrderUrl({ by: "favorites", time_range: "30d" });
            }}
            selected={sort.time_range === "30d"}
          >
            30 days
          </Menu.ItemButton>
          <Menu.ItemButton
            onClick={() => {
              setOrderUrl({ by: "favorites", time_range: "all" });
            }}
            selected={sort.time_range === "all"}
          >
            All
          </Menu.ItemButton>
        </Menu.Section>
      )}

      <Menu.Section title="Filter by...">
        {(activeTab === "queries" || activeTab === "dashboards") && (
          <Menu.ItemButton
            selected={showArchived}
            onClick={() => {
              setShowArchived(!showArchived);
            }}
          >
            <span>Show archived</span>
          </Menu.ItemButton>
        )}
        <Menu.ItemButton
          selected={publicness === "private"}
          onClick={() => {
            setPublicness(publicness === "private" ? "all" : "private");
          }}
        >
          <span>Show only private</span>
        </Menu.ItemButton>
        <Menu.ItemButton
          selected={publicness === "public"}
          onClick={() => {
            setPublicness(publicness === "public" ? "all" : "public");
          }}
        >
          <span>Show only public</span>
        </Menu.ItemButton>
        {activeTab !== "dashboards" && useIsFeatureEnabled("matviews") && (
          <Menu.ItemButton
            selected={showMaterialized}
            onClick={() => {
              setShowMaterialized(!showMaterialized);
            }}
          >
            <span>Show materialized views</span>
          </Menu.ItemButton>
        )}
      </Menu.Section>
    </Menu.Panel>
  );
}
